import { Button, Col, Form, Input, Row, Select, Space, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ContentWrapper } from "../../../Components/Layout/ContentWrapper";
import { useQuery } from "../../../Hooks/useQuery";

import { AdminEntry } from "../Constants";
import { divisionApi, subsidiaryApi, toArray } from "../../../Services";
import { useNotification } from "../../../Hooks/useNotification";
import { AdminRows, blankAdmin, findDuplicateAdmins } from "./AdminRows";
import { SuccessModal } from "../SuccessModal";
import workOrderStyles from "../../WorkOrder/WorkOrder.module.scss";

/**
 * Create / edit a Cost Center and its admins.
 *
 * Same shape as CreateSubsidiary — it adds the Cost Center Name field and
 * shares the admin rows via <AdminRows>. `?id=` switches it to edit.
 */
export const CreateCostCentre = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const id = query.get("id");

  const [subsidiary, setSubsidiary] = useState("");
  /* The saved subsidiary's name, as the detail gives it beside the id. */
  const [subsidiaryName, setSubsidiaryName] = useState("");
  const [costCentreName, setCostCentreName] = useState("");
  /* The record's versionOrder from the detail; update-division expects it back. */
  const [versionOrder, setVersionOrder] = useState<number | undefined>();
  const [admins, setAdmins] = useState<AdminEntry[]>([blankAdmin(0)]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [subsidiaryOptions, setSubsidiaryOptions] = useState<{ id: string; name: string }[]>([]);
  const { openNotification } = useNotification();

  /* The list the Subsidiary/Division tab reads, as the rest of the module does. */
  useEffect(() => {
    subsidiaryApi
      .list({ page: 0, size: 1000 })
      .then((rows: any) =>
        setSubsidiaryOptions(
          toArray(rows).map((row: any) => ({
            id: row.subsidiaryId ?? row.subsidiaryName,
            name: row.subsidiaryName ?? "",
          }))
        )
      )
      .catch(() => setSubsidiaryOptions([]));
  }, []);

  /**
   * A cost centre's admins default to the admins of its Subsidiary/Division.
   *
   * Picking a subsidiary and then naming the same people again by hand was
   * repetition: the subsidiary's admins are the answer in all but exceptional
   * cases. The rows stay editable for those, and this only runs on an explicit
   * pick, so the saved admins win when an existing cost centre is opened.
   */
  const applySubsidiaryAdmins = (subsidiaryId: string) => {
    if (!subsidiaryId) return;
    subsidiaryApi
      .detail(subsidiaryId)
      .then((record: any) => {
        const loaded = (record?.subsidiaryAdmins ?? []).map((admin: any, index: number) => ({
          key: `admin-${index}`,
          name: admin.userName ?? "",
          email: admin.email ?? "",
        }));
        setAdmins(loaded.length ? loaded : [blankAdmin(0)]);
      })
      .catch(() => undefined); // leave the rows as they are; they are editable
  };

  /* Edit mode: hydrate from the detail endpoint. */
  useEffect(() => {
    if (!id) return;
    divisionApi
      .detail(id)
      .then((record: any) => {
        setSubsidiary(record?.subsidiaryId ?? record?.subsidiary ?? "");
        setSubsidiaryName(record?.subsidiary ?? "");
        setCostCentreName(record?.division ?? "");
        setVersionOrder(record?.versionOrder);
        const loaded = (record?.divisionAdmins ?? []).map((admin: any, index: number) => ({
          key: `admin-${index}`,
          name: admin.userName ?? "",
          email: admin.email ?? "",
        }));
        setAdmins(loaded.length ? loaded : [blankAdmin(0)]);
      })
      .catch(() => undefined);
  }, [id]);

  /**
   * Friendlier copy for the clash the API reports as "The given cost centre
   * exists". The wording varies between endpoints — the subsidiary one says
   * "Already Exists" — so the test is on "exist" alone. Anything else is shown
   * as the API worded it.
   */
  const describe = (error: any) => {
    const message = String(error?.message ?? "").trim();
    if (/exist/i.test(message)) {
      return "Cost Center already exists! Please try with a new Cost Center name.";
    }
    return message || "Please try again.";
  };

  const handleSubmit = () => {
    setIsSaving(true);
    // The API calls this a "division"; the UI calls it a Cost Centre.
    const payload = {
      ...(id && { uniqueId: id }),
      ...(id && versionOrder !== undefined && versionOrder !== null && { versionOrder }),
      division: costCentreName.trim(),
      subsidiaryId: subsidiary,
      divisionAdmins: admins
        .filter((admin) => admin.name || admin.email)
        .map((admin) => ({ userName: admin.name, email: admin.email })),
    };
    /* Update where it was created: create-division writes the regular set,
       so the edit goes to update-division — update-default-division is the
       separate default set and did not hold these records. */
    const request = id ? divisionApi.update(payload) : divisionApi.create(payload);

    request
      .then(() => setSuccessOpen(true))
      .catch((err: any) =>
        openNotification({
          title: id ? "Failed to update" : "Failed to create",
          message: describe(err),
          type: "error",
        })
      )
      .finally(() => setIsSaving(false));
  };

  const backTo = "/approval-management?activeTab=costCenter";

  const isValid =
    subsidiary.trim().length > 0 &&
    costCentreName.trim().length > 0 &&
    admins.length > 0 &&
    admins.every((admin) => admin.name && admin.email) &&
    findDuplicateAdmins(admins).size === 0;

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(backTo)}
            icon="akar-icons:arrow-left"
            className={workOrderStyles.backIcon}
          />
          <Typography.Title
            level={3}
            style={{ margin: 0 }}
          >
            {id ? "Edit Cost Center" : "Create Cost Center"}
          </Typography.Title>
        </Space>
      }
    >
      <Form layout="vertical">
        <Typography.Title level={4}>Cost Center Details</Typography.Title>
        <Row gutter={24}>
          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Form.Item required label="Subsidiary/Division">
              <Select
                showSearch
                optionFilterProp="children"
                value={subsidiary || undefined}
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                onChange={(value: string) => {
                  setSubsidiary(value);
                  applySubsidiaryAdmins(value);
                }}
              >
                {/* A saved subsidiaryId the list does not carry printed as a bare
                    id, so it is offered under the name the detail gives it, in
                    place of any listed entry of that name. The id itself is kept. */}
                {(subsidiary && !subsidiaryOptions.some((option) => option.id === subsidiary)
                  ? [
                      { id: subsidiary, name: subsidiaryName || subsidiary },
                      ...subsidiaryOptions.filter((option) => option.name !== subsidiaryName),
                    ]
                  : subsidiaryOptions
                ).map((option, index) => (
                  <Select.Option
                    value={option.id}
                    key={`${index}`}
                  >
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Form.Item required label="Cost Center Name">
              <Input
                value={costCentreName}
                onChange={(e) => setCostCentreName(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title
          level={4}
          style={{ marginTop: 16 }}
        >
          Cost Center Admins
        </Typography.Title>

        <AdminRows
          admins={admins}
          setAdmins={setAdmins}
        />

        <Row
          justify="end"
          gutter={16}
          style={{ marginTop: 32 }}
        >
          <Col>
            <Button onClick={() => navigate(backTo)}>Cancel</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              disabled={!isValid}
              loading={isSaving}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Form>

      <SuccessModal
        open={successOpen}
        message="Cost Center saved successfully"
        onClose={() => {
          setSuccessOpen(false);
          navigate(backTo);
        }}
      />
    </ContentWrapper>
  );
};
